import type { WsItem } from "@storyboard/web-client";
import { firstAliasForFeed } from "@/store/feedSelectors";
import { idx, rowsByIndex, store } from "@/store/store";
import type { Item } from "@/store/types";

export async function markItemsContentLoaded(feedItemIds: string[]): Promise<void> {
  if (!feedItemIds?.length) return;
  store.updateMany("item", feedItemIds, (row) => (row.loadedContent ? row : { ...row, loadedContent: true }));
}

export async function resetAllItemsLoadedContent(): Promise<void> {
  store.updateMany("item", store.getAll("item").keys(), (row) =>
    row.loadedContent ? { ...row, loadedContent: false } : row,
  );
}

function contentExists(contentId: string): boolean {
  return (
    idx.filesByContent.keys(contentId).size > 0 ||
    idx.transcriptionsByContent.keys(contentId).size > 0 ||
    idx.audioEncodingsByContent.keys(contentId).size > 0 ||
    idx.linksByContent.keys(contentId).size > 0 ||
    idx.displayArtifactsByContent.keys(contentId).size > 0 ||
    idx.callRecordsByContent.keys(contentId).size > 0
  );
}

/**
 * Clears `loadedContent` on items whose artifacts never arrived, so the feed refetches them. Runs
 * on every feed open but only writes (and therefore only re-renders) when a row genuinely flips.
 */
export async function resetOrphanedLoadedContent(feedId: string): Promise<void> {
  store.updateMany("item", idx.itemsByFeed.keys(feedId), (row) =>
    row.loadedContent === true && !contentExists(row.contentId) ? { ...row, loadedContent: false } : row,
  );
}

function isFromAliasMember(feedId: string, accountId: string | null | undefined): boolean {
  if (accountId == null) return false;
  for (const key of idx.commandAliasesByFeed.keys(feedId)) {
    const alias = store.get("workspaceCommandAlias", key);
    if (!alias) continue;
    const membership = store.get("workspaceMembership", alias.workspaceMembershipId);
    if (membership?.accountId === accountId) return true;
  }
  return false;
}

export async function bulkUpsertItemAndCalculateMembership(
  itemRecords: WsItem[],
  options?: { fromAppSync?: boolean },
): Promise<Item[]> {
  if (itemRecords.length === 0) return [];
  const fromAppSync = options?.fromAppSync ?? false;

  return store.batch(() => {
    const data = itemRecords.map((itemRecord): Item => {
      return {
        id: itemRecord.id,
        feedId: itemRecord.feedId,
        accountId: itemRecord.accountId ?? null,
        contentId: itemRecord.contentId ?? "",
        createdAt: itemRecord.createdAt ?? new Date().toISOString(),
        updatedAt: itemRecord.updatedAt ?? itemRecord.createdAt ?? null,
        deletedAt: itemRecord.deletedAt ?? null,
        groupId: itemRecord.groupId ?? null,
        isSilent: itemRecord.isSilent || false,
        fromAppSync,
        isFromAliasMember: isFromAliasMember(itemRecord.feedId, itemRecord.accountId),
        isFromBot: itemRecord.accountId != null && (store.get("account", itemRecord.accountId)?.bot ?? false),
        firstInsert: true,
        loadedContent: false,
        unread: false,
        unreadNonMutable: false,
        status: "Completed",
        hasRegisteredSeenEvent: false,
        clientRecordedAt: itemRecord.clientRecordedAt ?? null,
        clientSentAt: itemRecord.clientSentAt ?? null,
        serverReceivedAt: itemRecord.serverReceivedAt ?? null,
      };
    });

    // The old ON CONFLICT SET only flipped firstInsert and took the incoming deletedAt/groupId,
    // preserving every client-maintained flag (unread, loadedContent, seen-event, ...).
    const results = store.upsert("item", data, (existing, next) => ({
      ...existing,
      firstInsert: false,
      deletedAt: next.deletedAt,
      groupId: next.groupId,
      // Set-only, as the alias stamp is: a re-ingestion after the feed's alias landed derives what the first missed.
      isFromAliasMember: existing.isFromAliasMember === true || next.isFromAliasMember === true,
      // Fall back to the stored value so a re-ingestion that omits the recording timestamps
      // (an optimistic insert, or a payload predating SVS-3411) can't wipe them.
      clientRecordedAt: next.clientRecordedAt ?? existing.clientRecordedAt ?? null,
      clientSentAt: next.clientSentAt ?? existing.clientSentAt ?? null,
      serverReceivedAt: next.serverReceivedAt ?? existing.serverReceivedAt ?? null,
    }));

    // Bump each touched feed's latestActivity to the batch's max createdAt (GREATEST semantics).
    const feedUpdatesMap = new Map<string, string>();
    for (const itemRecord of data) {
      const maxCreatedAt = feedUpdatesMap.get(itemRecord.feedId);
      if (!maxCreatedAt || itemRecord.createdAt > maxCreatedAt) {
        feedUpdatesMap.set(itemRecord.feedId, itemRecord.createdAt);
      }
    }
    store.updateMany("feed", feedUpdatesMap.keys(), (row) => {
      const maxCreatedAt = feedUpdatesMap.get(row.id);
      return maxCreatedAt !== undefined && (row.latestActivity == null || maxCreatedAt > row.latestActivity)
        ? { ...row, latestActivity: maxCreatedAt }
        : row;
    });

    return results;
  });
}

/** Feeds (with read permission) that currently hold unread, non-deleted items. */
function unreadFeeds({
  myCurrentWorkspaceMembershipId,
  requireNotificationPreference,
  teamAliases,
}: {
  myCurrentWorkspaceMembershipId: string;
  requireNotificationPreference: boolean;
  teamAliases?: string[];
}): { feedId: string; latestActivity: string | null }[] {
  const rows: { feedId: string; latestActivity: string | null }[] = [];
  const hasTeamFilter = teamAliases && teamAliases.length > 0;
  for (const permission of rowsByIndex("permission", idx.permissionsByMembership, myCurrentWorkspaceMembershipId)) {
    if (permission.name !== "read" || !permission.enabled || permission.feedId == null) continue;
    const feed = store.get("feed", permission.feedId);
    if (!feed) continue;
    if (requireNotificationPreference) {
      if (feed.isDm || feed.title == null) continue;
      const notifying = rowsByIndex("feedPreference", idx.feedPreferencesByFeed, feed.id).some(
        (p) => p.workspaceMembershipId === myCurrentWorkspaceMembershipId && p.pushNotificationPreference !== "none",
      );
      if (!notifying) continue;
    }
    if (hasTeamFilter && feed.isAliasFeed) {
      const alias = firstAliasForFeed(feed.id);
      if (alias == null || !teamAliases.includes(alias.alias)) continue;
    }
    const hasUnread = rowsByIndex("item", idx.itemsByFeed, feed.id).some(
      (item) => item.unread === true && item.deletedAt == null,
    );
    if (hasUnread) rows.push({ feedId: feed.id, latestActivity: feed.latestActivity });
  }
  return rows;
}

export const unreadFeedIdsCollections = [
  "item",
  "feed",
  "permission",
  "feedPreference",
  "workspaceCommandAlias",
] as const;

/** Stays a compute pipeline: per-feed unread aggregation over the membership's readable feeds. */
export function computeUnreadFeedIds(params: {
  myCurrentWorkspaceMembershipId: string;
  requireNotificationPreference?: boolean;
  limit?: number;
  teamAliases?: string[];
}): { feedId: string; latestActivity?: string | null }[] {
  const rows = unreadFeeds({
    myCurrentWorkspaceMembershipId: params.myCurrentWorkspaceMembershipId,
    requireNotificationPreference: params.requireNotificationPreference ?? false,
    teamAliases: params.teamAliases,
  });
  if (params.limit !== undefined) {
    rows.sort((a, b) => {
      const aActivity = a.latestActivity;
      const bActivity = b.latestActivity;
      if (aActivity === bActivity) return 0;
      if (aActivity == null) return -1;
      if (bActivity == null) return 1;
      return aActivity > bActivity ? -1 : 1;
    });
    return rows.slice(0, params.limit);
  }
  return rows.map(({ feedId }) => ({ feedId }));
}

export const fetchParentIds = async (contentIds: string[]): Promise<string[]> => {
  const wanted = new Set(contentIds);
  const parentIds: string[] = [];
  for (const item of store.getAll("item").values()) {
    if (wanted.has(item.contentId)) parentIds.push(item.id);
  }
  return parentIds;
};

function countUnreadFeeds({
  workspaceId,
  workspaceMembershipId,
  isAliasFeed,
  teamAliases,
}: {
  workspaceId: string;
  workspaceMembershipId: string;
  isAliasFeed: boolean;
  teamAliases?: string[];
}): { count: number }[] {
  const hasTeamFilter = isAliasFeed && teamAliases && teamAliases.length > 0;
  let count = 0;
  for (const permission of rowsByIndex("permission", idx.permissionsByMembership, workspaceMembershipId)) {
    if (permission.name !== "read" || !permission.enabled || permission.feedId == null) continue;
    const feed = store.get("feed", permission.feedId);
    if (!feed || feed.workspaceId !== workspaceId || feed.isAliasFeed !== isAliasFeed) continue;
    if (hasTeamFilter) {
      const alias = firstAliasForFeed(feed.id);
      if (alias == null || !teamAliases.includes(alias.alias)) continue;
    }
    const hasUnread = rowsByIndex("item", idx.itemsByFeed, feed.id).some(
      (item) => item.unread === true && item.deletedAt == null,
    );
    if (hasUnread) count++;
  }
  return [{ count }];
}

export const driverUnreadCountCollections = ["item", "feed", "permission", "workspaceCommandAlias"] as const;

/** Stays a compute pipeline: per-feed unread aggregation over alias feeds. */
export function computeDriverUnreadCount({
  workspaceId,
  workspaceMembershipId,
  teamAliases,
}: {
  workspaceId: string;
  workspaceMembershipId: string;
  teamAliases?: string[];
}): number {
  return countUnreadFeeds({ workspaceId, workspaceMembershipId, isAliasFeed: true, teamAliases })[0]?.count ?? 0;
}

export const channelUnreadCountCollections = ["item", "feed", "permission"] as const;

/** Stays a compute pipeline: per-feed unread aggregation over non-alias channels. */
export function computeChannelUnreadCount({
  workspaceId,
  workspaceMembershipId,
}: {
  workspaceId: string;
  workspaceMembershipId: string;
}): number {
  return countUnreadFeeds({ workspaceId, workspaceMembershipId, isAliasFeed: false })[0]?.count ?? 0;
}
